import React, { useState, useEffect } from "react";
import {
  Users,
  Plus,
  Trash2,
  X,
  Wallet,
  TrendingUp,
  TrendingDown,
  Calendar,
  Save,
  Printer,
  Loader2,
  Coins,
  Calculator,
  FileText,
} from "lucide-react";
import api from "../api/axiosInstance";

const PayrollManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [activeSlip, setActiveSlip] = useState(null);
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  const [form, setForm] = useState({
    employee_id: "",
    month: new Date().toLocaleString("default", {
      month: "long",
      year: "numeric",
    }),
    base_salary: 0,
    attendance_days: 30,
    multiplier: 1,
    additions: [
      { name: "Food Allowance", amount: 0 },
      { name: "Travel Allowance", amount: 0 },
    ],
    deductions: [
      { name: "PF Deduction", amount: 0 },
      { name: "EMI Deduction", amount: 0 },
    ],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [empRes, slipRes] = await Promise.all([
        api.get("get_employees.php"), // Using your employees table
        api.get("get_salary_slips.php"),
      ]);
      setEmployees(empRes.data.employees || []);
      setSlips(slipRes.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fill salary when employee is selected
  const handleEmployeeSelect = (id) => {
    const emp = employees.find((e) => e.id == id);
    if (emp) {
      setForm({
        ...form,
        employee_id: id,
        base_salary: emp.rate || 0, // Pre-filling from your 'rate' column
      });
    }
  };

  const calculatePayroll = () => {
    const perDaySalary = parseFloat(form.base_salary || 0) / 30;
    const attendancePay =
      perDaySalary *
      parseFloat(form.attendance_days || 0) *
      parseFloat(form.multiplier || 1);
    const totalAdditions = form.additions.reduce(
      (sum, i) => sum + parseFloat(i.amount || 0),
      0,
    );
    const totalDeductions = form.deductions.reduce(
      (sum, i) => sum + parseFloat(i.amount || 0),
      0,
    );
    return {
      attendancePay,
      totalAdditions,
      totalDeductions,
      gross: attendancePay + totalAdditions,
      net: attendancePay + totalAdditions - totalDeductions,
    };
  };

  const stats = calculatePayroll();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    try {
      const res = await api.post("save_salary_slip.php", { ...form, ...stats });
      if (res.data.status === "success") {
        setActiveSlip(null);
        loadData();
      }
    } catch (err) {
      alert("Error saving payroll");
    } finally {
      setSaveLoading(false);
    }
  };

  const downloadSlip = async (id, name, month) => {
    const res = await api.get(`generate_salary_slip_pdf.php?id=${id}`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Slip_${name}_${month}.pdf`);
    link.click();
  };

  if (loading)
    return (
      <div className="p-20 text-center font-black animate-pulse text-slate-300 uppercase">
        Loading Payroll...
      </div>
    );

  return (
    <div className="space-y-8 p-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-black text-slate-900 italic uppercase">
            Payroll Console
          </h1>
          <p className="text-slate-400 font-bold text-xs uppercase italic">
            Managing {employees.length} Active Employees
          </p>
        </div>
        <button
          onClick={() => setActiveSlip("new")}
          className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl flex items-center gap-2 hover:bg-indigo-600 transition-all"
        >
          <Plus size={18} /> New Salary Entry
        </button>
      </div>

      {/* List Table */}
      <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] font-black uppercase text-slate-400 border-b">
            <tr>
              <th className="p-6">Employee</th>
              <th className="p-6">Month</th>
              <th className="p-6 text-right">Base (Rate)</th>
              <th className="p-6 text-right text-rose-500">Deductions</th>
              <th className="p-6 text-right text-emerald-600">Net Paid</th>
              <th className="p-6 text-center">Slip</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {slips.map((s) => (
              <tr
                key={s.id}
                className="hover:bg-slate-50/50 transition-colors group"
              >
                <td className="p-6 font-black text-slate-900">
                  {s.employee_name}
                </td>
                <td className="p-6 font-bold text-slate-400">{s.month_year}</td>
                <td className="p-6 text-right font-bold italic">
                  ₹{parseFloat(s.base_salary).toLocaleString()}
                </td>
                <td className="p-6 text-right font-bold text-rose-400">
                  ₹{parseFloat(s.total_deductions).toLocaleString()}
                </td>
                <td className="p-6 text-right font-black text-slate-900 text-lg">
                  ₹{parseFloat(s.net_payable).toLocaleString()}
                </td>
                <td className="p-6 text-center">
                  <button
                    onClick={() =>
                      downloadSlip(s.id, s.employee_name, s.month_year)
                    }
                    className="p-2 text-slate-300 group-hover:text-indigo-600 transition-colors"
                  >
                    <Printer size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      {activeSlip && (
        <div className="fixed inset-0 z-[600] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={handleSubmit}
            className="bg-white w-full max-w-5xl rounded-[48px] shadow-2xl p-10 space-y-8 overflow-y-auto max-h-[95vh]"
          >
            <div className="flex justify-between items-center border-b pb-6">
              <h2 className="text-3xl font-black italic uppercase tracking-tighter">
                Process Monthly Salary
              </h2>
              <button
                type="button"
                onClick={() => setActiveSlip(null)}
                className="p-3 bg-slate-50 rounded-full hover:bg-rose-50 text-rose-500"
              >
                <X />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 bg-slate-50 p-8 rounded-[32px]">
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase text-slate-400">
                  Employee Name
                </label>
                <select
                  required
                  className="w-full p-3 bg-white rounded-xl font-bold outline-none border-2 border-transparent focus:border-indigo-100"
                  value={form.employee_id}
                  onChange={(e) => handleEmployeeSelect(e.target.value)}
                >
                  <option value="">Select Staff</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name} ({e.pay_type})
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase text-slate-400">
                  Base Rate (₹)
                </label>
                <input
                  type="number"
                  className="w-full p-3 bg-white rounded-xl font-bold border-2 border-indigo-50"
                  value={form.base_salary}
                  onChange={(e) =>
                    setForm({ ...form, base_salary: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase text-slate-400">
                  Attendance (Days)
                </label>
                <input
                  type="number"
                  step="0.5"
                  className="w-full p-3 bg-white rounded-xl font-bold"
                  value={form.attendance_days}
                  onChange={(e) =>
                    setForm({ ...form, attendance_days: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase text-indigo-500">
                  Multiplier / Bonus
                </label>
                <input
                  type="number"
                  step="0.1"
                  className="w-full p-3 bg-white border-2 border-indigo-100 rounded-xl font-black text-indigo-600"
                  value={form.multiplier}
                  onChange={(e) =>
                    setForm({ ...form, multiplier: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Positive Components */}
              <div className="space-y-4">
                <h3 className="text-[10px] font-black uppercase text-emerald-500 flex items-center gap-2 px-2">
                  <TrendingUp size={14} /> Earnings (Additions)
                </h3>
                {form.additions.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 bg-emerald-50/30 p-3 rounded-2xl border border-emerald-100 animate-in slide-in-from-left"
                  >
                    <input
                      className="flex-1 bg-transparent font-bold text-sm outline-none"
                      placeholder="Label"
                      value={item.name}
                      onChange={(e) => {
                        const n = [...form.additions];
                        n[idx].name = e.target.value;
                        setForm({ ...form, additions: n });
                      }}
                    />
                    <input
                      type="number"
                      placeholder="₹ 0"
                      className="w-24 p-2 bg-white rounded-lg font-black text-right outline-none"
                      value={item.amount}
                      onChange={(e) => {
                        const n = [...form.additions];
                        n[idx].amount = e.target.value;
                        setForm({ ...form, additions: n });
                      }}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          additions: form.additions.filter((_, i) => i !== idx),
                        })
                      }
                      className="text-rose-300 hover:text-rose-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      additions: [...form.additions, { name: "", amount: 0 }],
                    })
                  }
                  className="text-[9px] font-black text-emerald-600 uppercase ml-2 bg-emerald-50 px-3 py-1 rounded-full"
                >
                  + Add Allowance
                </button>
              </div>

              {/* Negative Components */}
              <div className="space-y-4">
                <h3 className="text-[10px] font-black uppercase text-rose-500 flex items-center gap-2 px-2">
                  <TrendingDown size={14} /> Deductions (Negative)
                </h3>
                {form.deductions.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 bg-rose-50/30 p-3 rounded-2xl border border-rose-100 animate-in slide-in-from-right"
                  >
                    <input
                      className="flex-1 bg-transparent font-bold text-sm outline-none"
                      placeholder="Label"
                      value={item.name}
                      onChange={(e) => {
                        const n = [...form.deductions];
                        n[idx].name = e.target.value;
                        setForm({ ...form, deductions: n });
                      }}
                    />
                    <input
                      type="number"
                      placeholder="₹ 0"
                      className="w-24 p-2 bg-white rounded-lg font-black text-right outline-none"
                      value={item.amount}
                      onChange={(e) => {
                        const n = [...form.deductions];
                        n[idx].amount = e.target.value;
                        setForm({ ...form, deductions: n });
                      }}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          deductions: form.deductions.filter(
                            (_, i) => i !== idx,
                          ),
                        })
                      }
                      className="text-rose-300 hover:text-rose-500"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      deductions: [...form.deductions, { name: "", amount: 0 }],
                    })
                  }
                  className="text-[9px] font-black text-rose-600 uppercase ml-2 bg-rose-50 px-3 py-1 rounded-full"
                >
                  + Add Deduction
                </button>
              </div>
            </div>

            {/* Final Calculation Summary */}
            <div className="bg-slate-900 rounded-[40px] p-10 text-white grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <p className="text-[9px] font-black opacity-40 uppercase">
                  Basic + Attendance
                </p>
                <p className="text-xl font-bold">
                  ₹{stats.attendancePay.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-[9px] font-black opacity-40 uppercase tracking-widest">
                  Allowances
                </p>
                <p className="text-xl font-bold text-emerald-400">
                  + ₹{stats.totalAdditions.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-[9px] font-black opacity-40 uppercase tracking-widest">
                  Deductions
                </p>
                <p className="text-xl font-bold text-rose-400">
                  - ₹{stats.totalDeductions.toLocaleString()}
                </p>
              </div>
              <div className="border-l border-white/10 pl-8">
                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">
                  Net Payable (In-Hand)
                </p>
                <h3 className="text-5xl font-black italic tracking-tighter">
                  ₹{stats.net.toLocaleString()}
                </h3>
              </div>
            </div>

            <button
              disabled={saveLoading}
              className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-xl shadow-2xl hover:bg-indigo-600 transition-all uppercase flex items-center justify-center gap-3"
            >
              {saveLoading ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Save size={24} />
              )}
              Process & Generate Salary Slip
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default PayrollManagement;
